import { useState, useEffect, useMemo } from 'react'
import { apiService } from '../../services/api'
import { Event } from '../../types'
import RichTextEditor from '../common/RichTextEditor'
import SafeHtmlContent from '../common/SafeHtmlContent'
import { sanitizeHtml } from '../../utils/sanitize'
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'

type SortField = 'title' | 'eventType' | 'startTime' | 'participants'
type SortDirection = 'asc' | 'desc'

export default function EventManagement() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [viewingParticipants, setViewingParticipants] = useState<Event | null>(null)
  const [participants, setParticipants] = useState<any[]>([])
  const [loadingParticipants, setLoadingParticipants] = useState(false)
  const [allUsers, setAllUsers] = useState<any[]>([])
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [sortField, setSortField] = useState<SortField>('startTime')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [hidePastEvents, setHidePastEvents] = useState(false)

  const emptyForm = {
    title: '', description: '',
    eventType: 'talk' as 'talk' | 'session' | 'bus' | 'competition' | 'other',
    startTime: '', endTime: '', location: '', maxParticipants: '', signupRequired: false,
  }
  const [formData, setFormData] = useState(emptyForm)

  useEffect(() => { loadEvents() }, [])
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t) }
  }, [toast])

  const loadEvents = async () => {
    setLoading(true); setError(null)
    const response = await apiService.getEvents()
    if (response.error) { setError(response.error.message); setLoading(false); return }
    if (response.data) {
      const data = response.data as any
      const arr = data?.data?.events || data?.events || []
      setEvents(Array.isArray(arr) ? arr : [])
    } else { setEvents([]) }
    setLoading(false)
  }

  const resetForm = () => { setFormData(emptyForm); setEditingEvent(null); setShowCreateForm(false) }

  const handleEdit = (event: Event) => {
    setEditingEvent(event)
    setFormData({
      title: event.title, description: event.description, eventType: event.eventType,
      startTime: event.startTime.slice(0, 16), endTime: event.endTime.slice(0, 16),
      location: event.location || '', maxParticipants: event.maxParticipants?.toString() || '',
      signupRequired: event.signupRequired,
    })
    setShowCreateForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Sanitize the HTML description before saving
    const sanitizedDescription = sanitizeHtml(formData.description)
    
    // Validate description is not empty after sanitization
    const plainText = sanitizedDescription.replace(/<[^>]*>/g, '').trim()
    if (!plainText) {
      setToast({ message: 'Description is required', type: 'error' })
      return
    }
    
    const eventData = {
      title: formData.title, description: sanitizedDescription, eventType: formData.eventType,
      startTime: new Date(formData.startTime).toISOString(), endTime: new Date(formData.endTime).toISOString(),
      location: formData.location || undefined,
      maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : undefined,
      signupRequired: formData.signupRequired,
    }
    const response = editingEvent
      ? await apiService.updateEvent(editingEvent.eventId, eventData)
      : await apiService.createEvent(eventData)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: editingEvent ? 'Event updated' : 'Event created', type: 'success' })
    resetForm(); loadEvents()
  }

  const handleDelete = async (eventId: string) => {
    const response = await apiService.deleteEvent(eventId)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: 'Event deleted', type: 'success' }); setDeleteConfirm(null); loadEvents()
  }

  const loadParticipants = async (event: Event) => {
    setViewingParticipants(event); setLoadingParticipants(true)
    try {
      // Load all users once and cache them
      let users = allUsers
      if (!usersLoaded) {
        const r = await apiService.getAllUsers()
        if (r.data) {
          const d = r.data as any
          users = d?.data?.users || d?.users || []
          if (Array.isArray(users)) { setAllUsers(users); setUsersLoaded(true) }
        }
      }
      // Look up each participant from the cached user list
      const details = event.participants.map((userId) => {
        const user = users.find((u: any) => u.userId === userId)
        return user || { userId, firstName: 'Unknown', lastName: 'User', email: 'N/A' }
      })
      setParticipants(details)
    } catch { setParticipants([]) }
    finally { setLoadingParticipants(false) }
  }

  // Sort and filter logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedAndFilteredEvents = useMemo(() => {
    let filtered = events

    // Filter out past events if toggle is on
    if (hidePastEvents) {
      const now = new Date()
      filtered = filtered.filter(event => new Date(event.endTime) >= now)
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title)
          break
        case 'eventType':
          comparison = a.eventType.localeCompare(b.eventType)
          break
        case 'startTime':
          comparison = new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
          break
        case 'participants':
          comparison = a.participants.length - b.participants.length
          break
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [events, sortField, sortDirection, hidePastEvents])

  if (loading) return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  )

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">{error}</p>
      <button onClick={loadEvents} className="mt-2 text-red-600 hover:text-red-800 font-medium">Try Again</button>
    </div>
  )

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Event Management</h2>
        <button onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          {showCreateForm ? 'Cancel' : 'Create Event'}
        </button>
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="evt-title" className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input id="evt-title" type="text" required value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label htmlFor="evt-desc" className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <RichTextEditor
                    content={formData.description}
                    onChange={(html) => setFormData({ ...formData, description: html })}
                    placeholder="Write your event description... Use the toolbar to format text."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="evt-type" className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
                    <select id="evt-type" required value={formData.eventType}
                      onChange={(e) => setFormData({ ...formData, eventType: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="talk">Talk</option>
                      <option value="session">Session</option>
                      <option value="bus">Bus</option>
                      <option value="competition">Competition</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="evt-location" className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input id="evt-location" type="text" value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="evt-start" className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                    <input id="evt-start" type="datetime-local" required value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label htmlFor="evt-end" className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                    <input id="evt-end" type="datetime-local" required value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input type="checkbox" checked={formData.signupRequired}
                      onChange={(e) => setFormData({ ...formData, signupRequired: e.target.checked })} className="mr-2" />
                    <span className="text-sm font-medium text-gray-700">Signup Required</span>
                  </label>
                  {formData.signupRequired && (
                    <input type="number" min="1" value={formData.maxParticipants}
                      onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                      placeholder="Max participants (optional)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  )}
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={resetForm}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
                  <button type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    {editingEvent ? 'Update Event' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={hidePastEvents}
            onChange={(e) => setHidePastEvents(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          Hide past events
        </label>
        <span className="text-sm text-gray-500">
          Showing {sortedAndFilteredEvents.length} of {events.length} events
        </span>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-1">
                    Event
                    <SortIcon field="title" currentField={sortField} direction={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('eventType')}
                >
                  <div className="flex items-center gap-1">
                    Type
                    <SortIcon field="eventType" currentField={sortField} direction={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('startTime')}
                >
                  <div className="flex items-center gap-1">
                    Date &amp; Time
                    <SortIcon field="startTime" currentField={sortField} direction={sortDirection} />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('participants')}
                >
                  <div className="flex items-center gap-1">
                    Participants
                    <SortIcon field="participants" currentField={sortField} direction={sortDirection} />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedAndFilteredEvents.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  {hidePastEvents && events.length > 0 ? 'No upcoming events. Uncheck "Hide past events" to see all.' : 'No events found. Create an event to get started.'}
                </td></tr>
              ) : sortedAndFilteredEvents.map((event) => {
                const isPast = new Date(event.endTime) < new Date()
                return (
                <tr key={event.eventId} className={`hover:bg-gray-50 ${isPast ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{event.title}</div>
                    <div className="text-sm text-gray-500 line-clamp-2">
                      <SafeHtmlContent content={event.description} className="text-sm text-gray-500" />
                    </div>
                    {event.location && <div className="text-sm text-gray-500">&#128205; {event.location}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">{event.eventType}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{new Date(event.startTime).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(event.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {isPast && <span className="text-xs text-gray-400 italic">Past</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.signupRequired ? (
                      event.participants.length > 0 ? (
                        <button onClick={() => loadParticipants(event)} className="text-blue-600 hover:text-blue-900 underline">
                          {event.participants.length}{event.maxParticipants && ` / ${event.maxParticipants}`}
                        </button>
                      ) : <span>0{event.maxParticipants && ` / ${event.maxParticipants}`}</span>
                    ) : <span>-</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => handleEdit(event)} className="text-blue-600 hover:text-blue-900">Edit</button>
                      {deleteConfirm === event.eventId ? (
                        <>
                          <button onClick={() => setDeleteConfirm(null)} className="text-gray-600 hover:text-gray-900">Cancel</button>
                          <button onClick={() => handleDelete(event.eventId)} className="text-red-600 hover:text-red-900">Confirm</button>
                        </>
                      ) : (
                        <button onClick={() => setDeleteConfirm(event.eventId)} className="text-red-600 hover:text-red-900">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Participant Modal */}
      {viewingParticipants && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Event Participants</h3>
                  <p className="text-sm text-gray-600 mt-1">{viewingParticipants.title}</p>
                </div>
                <button onClick={() => { setViewingParticipants(null); setParticipants([]) }}
                  className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingParticipants ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading participants...</p>
                </div>
              ) : participants.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No participants found</div>
              ) : (
                <div className="space-y-3">
                  {participants.map((p, i) => (
                    <div key={p.userId || i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{p.firstName} {p.lastName}</div>
                        <div className="text-sm text-gray-600">{p.email}</div>
                      </div>
                      <div className="text-sm text-gray-500">#{i + 1}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Total: {participants.length}{viewingParticipants.maxParticipants && ` / ${viewingParticipants.maxParticipants} max`}
              </div>
              <button onClick={() => { setViewingParticipants(null); setParticipants([]) }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// Sort direction indicator component
function SortIcon({ field, currentField, direction }: { field: SortField; currentField: SortField; direction: SortDirection }) {
  if (field !== currentField) {
    return <ArrowUpDown className="h-3 w-3 text-gray-400" />
  }
  return direction === 'asc'
    ? <ArrowUp className="h-3 w-3 text-blue-600" />
    : <ArrowDown className="h-3 w-3 text-blue-600" />
}
